import axios from 'axios';
import { BACKEND_URL } from './backend';

const API = `${BACKEND_URL}/api`;

// Configure axios to send cookies with every request
axios.defaults.withCredentials = true;

// CSRF: Read token from cookie and send in header with every state-changing request
axios.interceptors.request.use((config) => {
  const csrfToken = document.cookie
    .split('; ')
    .find(row => row.startsWith('csrf_token='))
    ?.split('=')[1];
  if (csrfToken) {
    config.headers['x-csrf-token'] = csrfToken;
  }
  return config;
});

const getAuthHeader = () => {
  return {};
};

export const api = {
  // Generic HTTP methods for flexibility
  get: (url) => axios.get(`${API}${url}`, { headers: getAuthHeader() }),
  post: (url, data) => axios.post(`${API}${url}`, data, { headers: getAuthHeader() }),
  put: (url, data) => axios.put(`${API}${url}`, data, { headers: getAuthHeader() }),
  delete: (url) => axios.delete(`${API}${url}`, { headers: getAuthHeader() }),
  
  // Artists
  getArtists: () => axios.get(`${API}/artists`),
  getArtist: (id) => axios.get(`${API}/artists/${id}`),
  getArtistReviews: (id) => axios.get(`${API}/artists/${id}/reviews`),
  getArtistRatingStats: (id) => axios.get(`${API}/artists/${id}/rating-stats`),
  replyToReview: (reviewId, reply) => axios.post(`${API}/artists/reviews/${reviewId}/reply`, { reply }),
  
  // Artist Setup (public)
  validateSetupToken: (token) => axios.get(`${API}/artist-setup/validate/${token}`),
  completeArtistSetup: (token, data) => axios.post(`${API}/artist-setup/complete/${token}`, data),
  uploadSetupPhoto: (token, formData) =>
    axios.post(`${API}/artist-setup/photo/${token}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  
  // Artist Self-Management
  getArtistProfile: () => axios.get(`${API}/artist/profile`, { headers: getAuthHeader() }),
  updateArtistProfile: (data) => axios.put(`${API}/artist/profile`, data, { headers: getAuthHeader() }),
  uploadArtistProfilePhoto: (formData) =>
    axios.post(`${API}/artist/profile/photo`, formData, { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }),
  requestVerification: () => axios.post(`${API}/artist/request-verification`, {}, { headers: getAuthHeader() }),
  
  // Artist Withdrawals
  requestWithdrawal: (amount) => axios.post(`${API}/artist/withdraw`, { amount }, { headers: getAuthHeader() }),
  getArtistWithdrawals: () => axios.get(`${API}/artist/withdrawals`, { headers: getAuthHeader() }),
  
  // Orders
  createOrder: (artistId, message) => 
    axios.post(`${API}/orders`, { artist_id: artistId, message }, { headers: getAuthHeader() }),
  
  getCheckoutStatus: (sessionId) =>
    axios.get(`${API}/orders/checkout-status/${sessionId}`, { headers: getAuthHeader() }),
  
  getMyOrders: () =>
    axios.get(`${API}/orders/my-orders`, { headers: getAuthHeader() }),
  
  getArtistOrders: () =>
    axios.get(`${API}/orders/artist-orders`, { headers: getAuthHeader() }),
  
  acceptOrder: (orderId) =>
    axios.post(`${API}/orders/${orderId}/accept`, {}, { headers: getAuthHeader() }),
  
  denyOrder: (orderId) =>
    axios.post(`${API}/orders/${orderId}/deny`, {}, { headers: getAuthHeader() }),
  
  deliverOrder: (orderId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/orders/${orderId}/deliver`, formData, {
      headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
    });
  },
  
  completeOrder: (orderId) =>
    axios.post(`${API}/orders/${orderId}/complete`, {}, { headers: getAuthHeader() }),

  cancelOrder: (orderId) =>
    axios.post(`${API}/orders/${orderId}/cancel`, {}, { headers: getAuthHeader() }),

  requestCancelOrder: (orderId) =>
    axios.post(`${API}/orders/${orderId}/request-cancel`, {}, { headers: getAuthHeader() }),

  approveCancelOrder: (orderId) =>
    axios.post(`${API}/orders/${orderId}/approve-cancel`, {}, { headers: getAuthHeader() }),

  denyCancelOrder: (orderId) =>
    axios.post(`${API}/orders/${orderId}/deny-cancel`, {}, { headers: getAuthHeader() }),
  
  // Reviews
  createReview: (orderId, rating, comment) =>
    axios.post(`${API}/orders/${orderId}/review`, { rating, comment }, { headers: getAuthHeader() }),

  getOrderReview: (orderId) =>
    axios.get(`${API}/orders/${orderId}/review`, { headers: getAuthHeader() }),
  
  // Notifications
  getNotifications: () =>
    axios.get(`${API}/notifications`, { headers: getAuthHeader() }),
  
  getUnreadCount: () =>
    axios.get(`${API}/notifications/unread-count`, { headers: getAuthHeader() }),
  
  markNotificationRead: (id) =>
    axios.post(`${API}/notifications/${id}/read`, {}, { headers: getAuthHeader() }),
  
  markAllNotificationsRead: () =>
    axios.post(`${API}/notifications/mark-all-read`, {}, { headers: getAuthHeader() }),
  
  deleteNotification: (id) =>
    axios.delete(`${API}/notifications/${id}`, { headers: getAuthHeader() }),
  
  deleteAllNotifications: () =>
    axios.delete(`${API}/notifications`, { headers: getAuthHeader() }),
  
  // Artist Earnings
  getArtistEarnings: () =>
    axios.get(`${API}/artist/earnings`, { headers: getAuthHeader() }),
  
  // Admin
  getAdminStats: () =>
    axios.get(`${API}/admin/stats`, { headers: getAuthHeader() }),

  getAdminHealth: () =>
    axios.get(`${API}/admin/health`, { headers: getAuthHeader() }),

  getPlatformStatus: () =>
    axios.get(`${API}/platform/status`),

  setLaunchStatus: (ordersEnabled) =>
    axios.put(`${API}/admin/platform/launch`, { orders_enabled: ordersEnabled }, { headers: getAuthHeader() }),

  exportArtistsCsv: () =>
    axios.get(`${API}/admin/artists/export`, { headers: getAuthHeader(), responseType: 'blob' }),
  
  getAdminEarnings: () =>
    axios.get(`${API}/admin/earnings`, { headers: getAuthHeader() }),
  
  getAllArtistsAdmin: () =>
    axios.get(`${API}/admin/artists`, { headers: getAuthHeader() }),
  
  createArtist: (data) =>
    axios.post(`${API}/admin/artists`, data, { headers: getAuthHeader() }),
  adminUploadArtistImage: (formData) =>
    axios.post(`${API}/admin/artists/upload-image`, formData, { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }),
  
  updateArtist: (id, data) =>
    axios.put(`${API}/admin/artists/${id}`, data, { headers: getAuthHeader() }),
  
  uploadArtistSample: (artistId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/admin/artists/${artistId}/upload-sample`, formData, {
      headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
    });
  },
  
  deleteArtist: (id) =>
    axios.delete(`${API}/admin/artists/${id}`, { headers: getAuthHeader() }),
  
  approveArtist: (id) =>
    axios.post(`${API}/admin/artists/${id}/approve`, {}, { headers: getAuthHeader() }),
  
  sendArtistSetupEmail: (id) =>
    axios.post(`${API}/admin/artists/${id}/send-setup-email`, {}, { headers: getAuthHeader() }),
  
  regenerateArtistToken: (id) =>
    axios.post(`${API}/admin/artists/${id}/regenerate-token`, {}, { headers: getAuthHeader() }),
  
  getAllUsers: () =>
    axios.get(`${API}/admin/users`, { headers: getAuthHeader() }),
  
  getUserDetails: (id) =>
    axios.get(`${API}/admin/users/${id}`, { headers: getAuthHeader() }),
  
  toggleArtistVerification: (id) =>
    axios.post(`${API}/admin/artists/${id}/verify`, {}, { headers: getAuthHeader() }),
  
  getPendingArtists: () =>
    axios.get(`${API}/admin/pending-artists`, { headers: getAuthHeader() }),
  
  getVerificationRequests: () =>
    axios.get(`${API}/admin/verification-requests`, { headers: getAuthHeader() }),
  
  getAllOrdersAdmin: () =>
    axios.get(`${API}/admin/orders`, { headers: getAuthHeader() }),
  
  // Admin Withdrawals
  getAllWithdrawals: () =>
    axios.get(`${API}/admin/withdrawals`, { headers: getAuthHeader() }),
  
  getPendingWithdrawals: () =>
    axios.get(`${API}/admin/withdrawals/pending`, { headers: getAuthHeader() }),
  
  processWithdrawal: (id) =>
    axios.post(`${API}/admin/withdrawals/${id}/process`, {}, { headers: getAuthHeader() }),
  
  rejectWithdrawal: (id) =>
    axios.post(`${API}/admin/withdrawals/${id}/reject`, {}, { headers: getAuthHeader() }),
  
  seedData: () =>
    axios.post(`${API}/admin/seed`, {}, { headers: getAuthHeader() }),
  
  linkArtistToUser: (artistId, userEmail) => {
    const formData = new FormData();
    formData.append('user_email', userEmail);
    return axios.post(`${API}/admin/link-artist/${artistId}`, formData, { headers: getAuthHeader() });
  },
  
  // Files
  getFileUrl: (filename) => `${API}/files/${filename}`,
  getAudioSampleUrl: (url) => url ? `${BACKEND_URL}${url}` : null,
  
  // Offers/Negotiations
  createOffer: (artistId, cartItemIds, proposedPrice, message) =>
    axios.post(`${API}/offers`, { artist_id: artistId, cart_item_ids: cartItemIds, proposed_price: proposedPrice, message }, { headers: getAuthHeader() }),
  
  getMyOffers: () =>
    axios.get(`${API}/offers/my-offers`, { headers: getAuthHeader() }),
  
  getArtistOffers: () =>
    axios.get(`${API}/offers/artist-offers`, { headers: getAuthHeader() }),
  
  acceptOffer: (offerId) =>
    axios.post(`${API}/offers/${offerId}/accept`, {}, { headers: getAuthHeader() }),
  
  denyOffer: (offerId) =>
    axios.post(`${API}/offers/${offerId}/deny`, {}, { headers: getAuthHeader() }),
  
  counterOffer: (offerId, counterPrice, message) =>
    axios.post(`${API}/offers/${offerId}/counter`, { counter_price: counterPrice, message }, { headers: getAuthHeader() }),
  
  userAcceptCounter: (offerId) =>
    axios.post(`${API}/offers/${offerId}/user-accept`, {}, { headers: getAuthHeader() }),
  
  userDeclineCounter: (offerId) =>
    axios.post(`${API}/offers/${offerId}/user-decline`, {}, { headers: getAuthHeader() }),
  
  checkoutOffer: (offerId) =>
    axios.post(`${API}/offers/${offerId}/checkout`, {}, { headers: getAuthHeader() }),
  
  getOfferCheckoutStatus: (offerId, sessionId) =>
    axios.get(`${API}/offers/${offerId}/checkout-status?session_id=${sessionId}`, { headers: getAuthHeader() }),
  
  cancelOffer: (offerId) =>
    axios.delete(`${API}/offers/${offerId}`, { headers: getAuthHeader() }),

  // Inbox / Direct Messaging
  startConversation: (artistId) =>
    axios.post(`${API}/inbox/conversations`, { artist_id: artistId }, { headers: getAuthHeader() }),
  
  getConversations: () =>
    axios.get(`${API}/inbox/conversations`, { headers: getAuthHeader() }),
  
  getConversation: (conversationId) =>
    axios.get(`${API}/inbox/conversations/${conversationId}`, { headers: getAuthHeader() }),
  
  getConversationMessages: (conversationId) =>
    axios.get(`${API}/inbox/conversations/${conversationId}/messages`, { headers: getAuthHeader() }),
  
  sendDirectMessage: (conversationId, content) =>
    axios.post(`${API}/inbox/conversations/${conversationId}/messages`, { content }, { headers: getAuthHeader() }),
  
  sendQuote: (conversationId, price, description, buyerMessage, deliveryDays) =>
    axios.post(`${API}/inbox/conversations/${conversationId}/quote`, {
      price,
      description,
      buyer_message: buyerMessage,
      delivery_days: deliveryDays
    }, { headers: getAuthHeader() }),
  
  acceptQuote: (messageId) =>
    axios.post(`${API}/inbox/quotes/${messageId}/accept`, {}, { headers: getAuthHeader() }),
  
  declineQuote: (messageId) =>
    axios.post(`${API}/inbox/quotes/${messageId}/decline`, {}, { headers: getAuthHeader() }),
  
  markConversationRead: (conversationId) =>
    axios.put(`${API}/inbox/conversations/${conversationId}/read`, {}, { headers: getAuthHeader() }),

  sendTyping: (conversationId, isTyping) =>
    axios.post(`${API}/inbox/conversations/${conversationId}/typing`, { is_typing: isTyping }, { headers: getAuthHeader() }),
  
  getInboxUnreadCount: () =>
    axios.get(`${API}/inbox/unread-count`, { headers: getAuthHeader() }),
  
  deleteConversation: (conversationId) =>
    axios.delete(`${API}/inbox/conversations/${conversationId}`, { headers: getAuthHeader() }),
  
  deleteMessage: (conversationId, messageId) =>
    axios.delete(`${API}/inbox/conversations/${conversationId}/messages/${messageId}`, { headers: getAuthHeader() }),

  // Settings
  getProfile: () =>
    axios.get(`${API}/settings/profile`, { headers: getAuthHeader() }),

  updateProfile: (data) =>
    axios.put(`${API}/settings/profile`, data, { headers: getAuthHeader() }),

  changePassword: (data) =>
    axios.put(`${API}/settings/password`, data, { headers: getAuthHeader() }),

  getPaymentMethods: () =>
    axios.get(`${API}/settings/payment-methods`, { headers: getAuthHeader() }),

  updatePaymentMethods: (data) =>
    axios.put(`${API}/settings/payment-methods`, data, { headers: getAuthHeader() }),

  uploadAvatar: (formData) =>
    axios.post(`${API}/settings/avatar`, formData, { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }),

  // Stripe Connect
  connectBank: (data) =>
    axios.post(`${API}/settings/connect-bank`, data, { headers: getAuthHeader() }),

  getConnectStatus: () =>
    axios.get(`${API}/settings/connect-status`, { headers: getAuthHeader() }),

  refreshConnectLink: (data) =>
    axios.post(`${API}/settings/connect-refresh`, data, { headers: getAuthHeader() }),

  disconnectBank: () =>
    axios.post(`${API}/settings/disconnect-bank`, {}, { headers: getAuthHeader() }),

  // Disputes
  createDispute: (orderId, reason, description) =>
    axios.post(`${API}/disputes`, { order_id: orderId, reason, description }, { headers: getAuthHeader() }),
  
  getMyDisputes: () =>
    axios.get(`${API}/disputes`, { headers: getAuthHeader() }),
  
  getDispute: (disputeId) =>
    axios.get(`${API}/disputes/${disputeId}`, { headers: getAuthHeader() }),
  
  // Admin Disputes
  getAllDisputes: () =>
    axios.get(`${API}/disputes/admin/all`, { headers: getAuthHeader() }),
  
  updateDisputeStatus: (disputeId, status, adminNotes) =>
    axios.put(`${API}/disputes/admin/${disputeId}/status`, { status, admin_notes: adminNotes }, { headers: getAuthHeader() }),
  
  resolveDispute: (disputeId, resolutionType, refundAmount, resolutionNotes) =>
    axios.post(`${API}/disputes/admin/${disputeId}/resolve`, {
      resolution_type: resolutionType,
      refund_amount: refundAmount,
      resolution_notes: resolutionNotes
    }, { headers: getAuthHeader() }),

  // Audit Logs
  getAuditLogs: (params) =>
    axios.get(`${API}/admin/audit-logs`, { params, headers: getAuthHeader() }),
  
  getAuditLogActions: () =>
    axios.get(`${API}/admin/audit-logs/actions`, { headers: getAuthHeader() }),

  // Waitlist (admin)
  getWaitlist: () =>
    axios.get(`${API}/admin/waitlist`, { headers: getAuthHeader() }),

  exportWaitlist: () =>
    axios.get(`${API}/admin/waitlist/export`, {
      headers: getAuthHeader(),
      responseType: 'blob',
    }),

  inviteWaitlistArtist: (entryId) =>
    axios.post(`${API}/admin/waitlist/${entryId}/invite`, {}, { headers: getAuthHeader() }),

  // Studio (artists)
  getInstrumentals: () =>
    axios.get(`${API}/studio/instrumentals`, { headers: getAuthHeader() }),

  uploadInstrumental: (formData) =>
    axios.post(`${API}/studio/instrumentals`, formData, { headers: getAuthHeader() }),

  deleteInstrumental: (id) =>
    axios.delete(`${API}/studio/instrumentals/${id}`, { headers: getAuthHeader() }),

  getStudioExports: () =>
    axios.get(`${API}/studio/exports`, { headers: getAuthHeader() }),

  beginStudioExport: () =>
    axios.post(`${API}/studio/exports/begin`, {}, { headers: getAuthHeader() }),
  uploadStudioExportChunk: (formData, onUploadProgress) =>
    axios.post(`${API}/studio/exports/chunk`, formData, { headers: getAuthHeader(), onUploadProgress }),
  finalizeStudioExport: (formData) =>
    axios.post(`${API}/studio/exports/finalize`, formData, { headers: getAuthHeader() }),

  deleteStudioExport: (id) =>
    axios.delete(`${API}/studio/exports/${id}`, { headers: getAuthHeader() }),

  downloadStudioExportZip: (id) =>
    axios.get(`${API}/studio/exports/${id}/zip`, { headers: getAuthHeader(), responseType: 'blob' }),

  deliverStudioExport: (exportId, orderId) =>
    axios.post(`${API}/studio/exports/${exportId}/deliver/${orderId}`, {}, { headers: getAuthHeader() }),

  // Privacy / GDPR
  exportMyData: () =>
    axios.get(`${API}/privacy/export`, { headers: getAuthHeader(), responseType: 'blob' }),
  requestAccountDeletion: () =>
    axios.post(`${API}/privacy/delete-account`, {}, { headers: getAuthHeader() }),

  // Privacy admin console
  adminListPendingDeletions: () =>
    axios.get(`${API}/admin/privacy/pending-deletions`, { headers: getAuthHeader() }),
  adminCancelPendingDeletion: (userId) =>
    axios.post(`${API}/admin/privacy/pending-deletions/${userId}/cancel`, {}, { headers: getAuthHeader() }),
  adminListPrivacyLog: (params = {}) =>
    axios.get(`${API}/admin/privacy/log`, { headers: getAuthHeader(), params }),
  adminExportPrivacyLogCsv: (params = {}) =>
    axios.get(`${API}/admin/privacy/log.csv`, { headers: getAuthHeader(), params, responseType: 'blob' }),

  // Studio library (admin)
  adminGetInstrumentals: () =>
    axios.get(`${API}/admin/studio/instrumentals`, { headers: getAuthHeader() }),

  adminUploadInstrumental: (formData, onUploadProgress) =>
    axios.post(`${API}/admin/studio/instrumentals`, formData, {
      headers: getAuthHeader(),
      onUploadProgress,
    }),

  adminUpdateInstrumental: (id, data) =>
    axios.patch(`${API}/admin/studio/instrumentals/${id}`, data, { headers: getAuthHeader() }),

  // Weekly Specials (public)
  getWeeklySpecials: () =>
    axios.get(`${API}/weekly-specials`),

  // Agent self-service
  getAgentProfile: () =>
    axios.get(`${API}/agent/profile`, { headers: getAuthHeader() }),

  updateAgentProfile: (data) =>
    axios.put(`${API}/agent/profile`, data, { headers: getAuthHeader() }),

  getAgentEarnings: () =>
    axios.get(`${API}/agent/earnings`, { headers: getAuthHeader() }),

  getAgentWithdrawals: () =>
    axios.get(`${API}/agent/withdrawals`, { headers: getAuthHeader() }),

  requestAgentWithdrawal: (amount) =>
    axios.post(`${API}/agent/withdraw`, { amount }, { headers: getAuthHeader() }),

  getAgentOrders: () =>
    axios.get(`${API}/agent/orders`, { headers: getAuthHeader() }),

  getAgentLineup: () =>
    axios.get(`${API}/agent/lineup`, { headers: getAuthHeader() }),

  getAgentAvailableArtists: () =>
    axios.get(`${API}/agent/available-artists`, { headers: getAuthHeader() }),

  setAgentLineup: (items) =>
    axios.put(`${API}/agent/lineup`, { items }, { headers: getAuthHeader() }),

  addGuestArtist: (name, price) =>
    axios.post(`${API}/agent/lineup/guest`, { name, price }, { headers: getAuthHeader() }),

  removeGuestArtist: (artistId) =>
    axios.delete(`${API}/agent/lineup/guest/${artistId}`, { headers: getAuthHeader() }),

  // Admin → guest artists (pending review queue)
  getAdminGuestArtists: () =>
    axios.get(`${API}/admin/guest-artists`, { headers: getAuthHeader() }),

  updateAdminGuestArtist: (id, data) =>
    axios.put(`${API}/admin/guest-artists/${id}`, data, { headers: getAuthHeader() }),

  promoteAdminGuestArtist: (id) =>
    axios.post(`${API}/admin/guest-artists/${id}/promote`, {}, { headers: getAuthHeader() }),

  deleteAdminGuestArtist: (id) =>
    axios.delete(`${API}/admin/guest-artists/${id}`, { headers: getAuthHeader() }),

  // Admin → agents
  getAdminAgents: () =>
    axios.get(`${API}/admin/agents`, { headers: getAuthHeader() }),

  createAdminAgent: (data) =>
    axios.post(`${API}/admin/agents`, data, { headers: getAuthHeader() }),

  updateAdminAgent: (id, data) =>
    axios.put(`${API}/admin/agents/${id}`, data, { headers: getAuthHeader() }),

  deleteAdminAgent: (id) =>
    axios.delete(`${API}/admin/agents/${id}`, { headers: getAuthHeader() }),

  // Test Mode (admin only)
  getTestMode: () =>
    axios.get(`${API}/admin/test-mode`, { headers: getAuthHeader() }),

  getTestModeStatus: () =>
    axios.get(`${API}/admin/test-mode/me`, { headers: getAuthHeader() }),

  enterTestMode: (personaRole) =>
    axios.post(`${API}/admin/test-mode/${personaRole}`, {}, { headers: getAuthHeader() }),

  resetTestMode: () =>
    axios.post(`${API}/admin/test-mode/reset`, {}, { headers: getAuthHeader() }),

  exitTestMode: () =>
    axios.delete(`${API}/admin/test-mode`, { headers: getAuthHeader() }),

  // Order creation now supports an optional agent_id (Weekly Specials routing)
  createOrderViaAgent: (artistId, message, agentId) =>
    axios.post(`${API}/orders`, { artist_id: artistId, message, agent_id: agentId }, { headers: getAuthHeader() }),
};

export default api;
